import React, { ReactNode, useEffect, useId, useRef, useState } from "react";
import styles from "./optionBlock.module.css";
import Tooltip from "@mui/material/Tooltip";
import { Typography } from "@mui/material";
import { CoreChip, CoreChipProps, CoreInfoIndicator, CoreRadioCardIcon, CoreSwitch } from "../../core";
import { useFocusElementOnMount, useMobileLayout } from "../../hooks";
import { ProctoringPlatforms } from "@verity/proctoring-option/types";
import i18nPackage from "../../i18n/i18n";
import {
	GROUPED_OPTION_TEXT,
	isGroupedOptionKey,
	PROCTORING_OPTION_TEXT,
	RenderableProctoringKey,
} from "./option-text/ProctoringOptionText";
import { useProctoringOptionContext } from "../context/EditQuizContext";
import { useProctoringHelpers } from "./hooks/useProctoringHelpers";

export type OptionBlockProps = {
	optionKey: RenderableProctoringKey;

	/**Overrides */
	description?: ReactNode;
	/**Overrides */
	contentHeader?: string;
	/**Overrides */
	Icon?: React.ElementType;

	/**Overrides. Default: !isOptionAllowed(key) || enforcedProfilesOnly */
	disabled?: boolean;
	/**Overrides. Default: getDisabledMessage(key) */
	disabledMessage?: React.ReactNode;

	/**Renders a info icon next to the header */
	hint?: React.ReactNode;

	checked: boolean;
	updateState?: (state: boolean) => void;
	/**Will be aligned with the description */
	menuItems?: React.ReactNode;
	/** The width of the toggle under which the icon will be hidden. */
	compactWidth?: number;

	/**Overrides. Default:  getPartiallySupportedPlatforms(optionKey)*/
	partiallySupportedPlatforms?: Set<keyof ProctoringPlatforms>;
	/**Aligned with the icon. Takes up full width of option block */
	children?: React.ReactNode;
};

function OptionBlockTooltip({ tooltip }: { tooltip: React.ReactNode }) {
	return (
		<Tooltip
			title={
				<div className={styles.infoTextContainer}>
					{typeof tooltip === "string" ? <Typography className={styles.infoText}>{tooltip}</Typography> : tooltip}
				</div>
			}
			PopperProps={{
				modifiers: [
					{
						name: "arrow",
						options: {
							padding: 0,
						},
					},
					{
						name: "offset",
						options: {
							offset: [0, 10],
						},
					},
					{
						name: "preventOverflow",
						options: {
							padding: 8,
						},
					},
					{
						name: "computeStyles",
						options: {
							adaptive: false,
						},
					},
					{
						name: "arrowCenterFix",
						enabled: true,
						phase: "write",
						fn({ state }) {
							const arrow = state.elements.arrow;
							if (arrow) {
								arrow.style.left = "50%";
								arrow.style.transform = "translateX(-50%)";
							}
						},
					},
				],
			}}
		>
			<div
				style={{
					top: 0,
					left: 0,
					position: "absolute",
					height: "80%",
					width: "100%",
					zIndex: 0,
				}}
			/>
		</Tooltip>
	);
}

function OptionBlockContainer({
	disabled,
	disabledMessage,
	compactWidth,
	children,
	optionKey,
}: Pick<OptionBlockProps, "disabled" | "disabledMessage" | "compactWidth" | "optionKey"> & {
	children: ReactNode;
}) {
	const [compacted, setCompacted] = useState(false);
	const wrapperRef = useRef<HTMLDivElement | null>(null);

	const { getDisabledMessage } = useProctoringHelpers();

	const finalDisabledMessage =
		disabledMessage ?? (!isGroupedOptionKey(optionKey) ? getDisabledMessage(optionKey) : undefined);

	useEffect(() => {
		if (!wrapperRef.current || compactWidth === undefined) {
			return;
		}

		const resizeObserver = new ResizeObserver(() => {
			setTimeout(() => {
				if (wrapperRef.current?.clientWidth && wrapperRef.current.clientWidth < compactWidth) {
					if (!compacted) {
						setCompacted(true);
					}
				} else if (compacted) {
					setCompacted(false);
				}
			}, 0);
		});
		resizeObserver.observe(wrapperRef.current);
		return function cleanup() {
			resizeObserver.disconnect();
		};
	}, [compacted, compactWidth]);

	return (
		<div className={styles.optionBoxWrapper} ref={wrapperRef}>
			{children}
			{disabled && finalDisabledMessage && <OptionBlockTooltip tooltip={finalDisabledMessage} />}
		</div>
	);
}

function OptionBlockDescription({ optionKey, description }: Pick<OptionBlockProps, "description" | "optionKey">) {
	const isGroupedKey = isGroupedOptionKey(optionKey);

	const textConfig = isGroupedKey ? GROUPED_OPTION_TEXT[optionKey] : PROCTORING_OPTION_TEXT[optionKey];
	const finalDescription = description ?? textConfig.description;
	return <Typography className={styles.optionDescriptionText}>{finalDescription}</Typography>;
}

function OptionBlockInfo({
	contentHeader,
	description,
	Icon,
	disabled,
	id,
	partiallySupportedPlatforms,
	optionKey,
	checked,
	hint,
}: Pick<
	OptionBlockProps,
	| "contentHeader"
	| "description"
	| "Icon"
	| "disabled"
	| "partiallySupportedPlatforms"
	| "optionKey"
	| "checked"
	| "hint"
> & {
	id?: string;
}) {
	const isGroupedKey = isGroupedOptionKey(optionKey);
	const { isMobileLayout } = useProctoringOptionContext();
	const { getPartiallySupportedPlatforms } = useProctoringHelpers();

	const textConfig = isGroupedKey ? GROUPED_OPTION_TEXT[optionKey] : PROCTORING_OPTION_TEXT[optionKey];

	const header = contentHeader ?? textConfig.header;
	const DefaultIcon = textConfig.icon;

	const supportedPlatforms =
		partiallySupportedPlatforms ?? (isGroupedKey ? undefined : getPartiallySupportedPlatforms(optionKey));

	return (
		<>
			<div className={styles.iconContainer}>
				{Icon && <Icon aria-hidden="true" />}
				{DefaultIcon && !Icon && (
					<CoreRadioCardIcon enabled={checked && !disabled} icon={<DefaultIcon aria-hidden="true" />} showCheckmark />
				)}
			</div>
			<div className={styles.optionBlockCol}>
				<div className={`${styles.optionTitleWrapper} ${disabled ? styles.disabled : ""}`}>
					<Typography variant="h3" className={styles.optionTitleText} id={id}>
						{header}
					</Typography>
					{hint && <CoreInfoIndicator hint={hint} size={20} />}
					{Array.from(supportedPlatforms ?? []).map((platform) => (
						<OptionBlockPlatformBadge key={platform} platform={platform} />
					))}
				</div>
				{!isMobileLayout && <OptionBlockDescription description={description} optionKey={optionKey} />}
			</div>
		</>
	);
}

export function OptionBlockPlatformBadge({
	platform,
	chipProps,
}: {
	platform: keyof ProctoringPlatforms;
	chipProps?: Omit<CoreChipProps, "chipColor" | "children">;
}) {
	const badgeTextKeyByPlatform = {
		isExtension: "extension",
		isLockdown: "lockdown",
		isMobile: "mobile",
		isChromeOs: "chromeOs",
	} as const;

	const chipColorByPlatform = {
		isExtension: "#F4ECFA",
		isLockdown: "#EAF6F6",
		isMobile: "#DFF2F2",
		isChromeOs: "#F5E5F4",
	} as const;

	const badgeTextKey = badgeTextKeyByPlatform[platform];
	const chipColor = chipColorByPlatform[platform];
	return (
		<CoreChip
			chipColor={chipColor}
			variant="rectangular"
			className={styles.platformBadge}
			tooltip={i18nPackage.t(`editQuizPage.proctoringOptionsTextTSX.badge.${badgeTextKey}.tooltip`)}
			{...chipProps}
		>
			{i18nPackage.t(`editQuizPage.proctoringOptionsTextTSX.badge.${badgeTextKey}.badge`)}
		</CoreChip>
	);
}

export default function OptionBlock({
	description,
	contentHeader,
	Icon,
	disabled,
	disabledMessage,
	checked,
	menuItems,
	compactWidth = 225,
	updateState,
	partiallySupportedPlatforms,
	optionKey,
	children,
	hint,
}: OptionBlockProps) {
	const headerId = useId();
	const isMobileLayout = useMobileLayout();

	const { onFocusProctoringOption, proctoringOptionToFocus, enforcedProfilesOnly } = useProctoringOptionContext();

	const { ref } = useFocusElementOnMount<HTMLDivElement>(
		proctoringOptionToFocus && proctoringOptionToFocus === optionKey,
		() => onFocusProctoringOption?.(),
	);

	const { isOptionAllowed } = useProctoringHelpers();

	// Disabled if enforcedProfile is true, otherwise check isOptionAllowed if it is a proctoring key
	const finalDisabled =
		disabled ?? (enforcedProfilesOnly || (!isGroupedOptionKey(optionKey) && !isOptionAllowed(optionKey)));

	return (
		<OptionBlockContainer
			optionKey={optionKey}
			disabled={finalDisabled}
			disabledMessage={disabledMessage}
			compactWidth={compactWidth}
		>
			<div className={styles.optionBlock} ref={ref}>
				<OptionBlockInfo
					contentHeader={contentHeader}
					description={description}
					Icon={Icon}
					disabled={finalDisabled}
					id={headerId}
					partiallySupportedPlatforms={partiallySupportedPlatforms}
					optionKey={optionKey}
					checked={checked}
					hint={hint}
				/>
				{updateState && (
					<CoreSwitch
						checked={checked}
						onChange={(event) => {
							const newChecked = event.target.checked;
							if (!finalDisabled) {
								updateState(newChecked);
							}
						}}
						disabled={finalDisabled && !checked}
						inputProps={{ "aria-labelledby": headerId }}
						labelTextType={isMobileLayout ? "none" : "left"}
					/>
				)}
				{isMobileLayout && <OptionBlockDescription description={description} optionKey={optionKey} />}
				{checked && menuItems && <div className={styles.menuItemsWrapper}>{menuItems}</div>}
			</div>
			{children}
		</OptionBlockContainer>
	);
}
